import { Card } from 'antd';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';

import ModelBreadcrumbs from '../../../../components/ModelBreadcrumbs';
import { RefreshButton } from '../../../../components/RefreshButton';
import { ITab, TabsManager } from '../../../../components/TabsManager';
import { apiClient } from '../../../../graphql/api/apiClient';
import { usevirtualDirectoryAuditQuery } from '../../../../graphql/api/generated';
import { PermissionKey_enum, RoleKey_enum } from '../../../../graphql/hasura/generated';
import { useTabSelectorWithHistory } from '../../../../hooks/useTabSelector';
import BasicLayout from '../../../../layouts/BasicLayout';
import ErrorLayout from '../../../../layouts/ErrorLayout';
import { authentication } from '../../../../stores';
import { PropertyModel } from '../../model';

import { PropertyVirtualDirectoryForm } from './PropertyVirtualDirectoryForm';
import { VirtualDirectoryResidents } from './VirtualDirectoryResidents';
import { VirtualDirectoryUnits } from './VirtualDirectoryUnits';

export const PropertyVirtualDirectoryPage: React.FC<RouteComponentProps<{ propertyId: string }>> = (props) => {
  const { propertyId } = props.match.params;

  const tabSelector = useTabSelectorWithHistory({
    defaultActiveKey: 'SETTINGS',
    allowedKeys: ['SETTINGS', 'RESIDENTS', 'UNITS'],
    replace: true,
  });

  const { previousData, data, error, loading, refetch } = usevirtualDirectoryAuditQuery({
    client: apiClient,
    variables: {
      input: { propertyId },
    },
  });

  const pageTitle = 'Virtual Directory';

  if (error) {
    return <ErrorLayout pageTitle={pageTitle} error={error} />;
  }

  const audit = data?.virtualDirectoryAudit || previousData?.virtualDirectoryAudit;
  const property = audit?.property;

  const users = audit?.users || [];
  const units = audit?.units || [];
  const userUnlistedReasonFilters = audit?.userUnlistedReasonFilters || [];
  const unitUnlistedReasonFilters = audit?.unitUnlistedReasonFilters || [];

  const { activeKey } = tabSelector;
  const csvExportTitle = `virtual_directory_${activeKey}_${property?.name}`;

  const tabs: ITab[] = [
    {
      loading: !property,
      key: 'SETTINGS',
      title: 'Settings',
      empty: false,
      content: () => {
        return property ? <PropertyVirtualDirectoryForm property={property} /> : null;
      },
    },
  ];

  const canViewResidents = (
    authentication.hasPermission(PermissionKey_enum.User_Read) ||
    authentication.currentRoleRelationships.some(r => (
      r.canReadUserRole && r.relatedRole.key === RoleKey_enum.RESIDENT
    )) || false
  );

  if (canViewResidents) {
    tabs.push({
      loading: false,
      key: 'RESIDENTS',
      title: 'Residents',
      empty: false,
      content: () => (
        <VirtualDirectoryResidents
          residents={users}
          unlistedReasonFilters={userUnlistedReasonFilters}
          csvExportTitle={csvExportTitle}
          loading={loading}
        />
      ),
    });
  }

  const canViewUnits = authentication.hasPermission(PermissionKey_enum.Unit_Read);

  if (canViewUnits) {
    tabs.push(
      {
        loading: false,
        key: 'UNITS',
        title: 'Units',
        empty: false,
        content: () => (
          <VirtualDirectoryUnits
            units={units}
            unlistedReasonFilters={unitUnlistedReasonFilters}
            canViewResidents={canViewResidents}
            csvExportTitle={csvExportTitle}
            loading={loading}
            refetch={refetch}
          />
        ),
      },
    );
  }

  return (
    <BasicLayout pageTitle={pageTitle}>
      <Card
        title={(
          <ModelBreadcrumbs
            model={PropertyModel}
            rowBreadCrumb={{ row: property }}
            customBreadCrumbs={[pageTitle]}
          />
        )}
        extra={(
          <RefreshButton
            disabled={loading}
            refresh={refetch}
            useSubscription={false}
          />
        )}
        bordered={false}
        style={{ marginBottom: '20px' }}
      >
        <TabsManager
          tabs={tabs}
          tabSelector={tabSelector}
          cardBodyStyle={{ padding: 0 }}
        />
      </Card>
    </BasicLayout>
  );
}
